"use client";

import { ArrowUpRight, Bot, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { usePathname } from "../lib/router";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CustomerChat } from "./customer-chat";
import {
  getPageAssistantContent,
  type PageAssistantTemplate,
} from "../lib/ai-customer-service";

export function AiCustomerService({
  onOpenChat,
}: {
  onOpenChat?: (prompt?: string) => void;
}) {
  const pathname = usePathname() ?? "/";
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [intro, setIntro] = useState<PageAssistantTemplate | null>(null);

  useEffect(() => {
    const templates = getPageAssistantContent(pathname).templates;

    setIntro((current) => {
      let index = Math.floor(Math.random() * templates.length);
      if (templates.length > 1 && templates[index].title === current?.title)
        index = (index + 1) % templates.length;
      return templates[index];
    });
    setIsExpanded(true);
  }, [pathname]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleOpenChat = () => {
    if (onOpenChat) onOpenChat(intro?.prompt);
    else setChatOpen(true);
  };

  return (
    <>
      {isMounted &&
        createPortal(
          <AnimatePresence initial={false} mode="wait">
            {isExpanded && intro && (
              <motion.aside
                className="ai-customer-service-card"
                key={`${pathname}-${intro.title}`}
                aria-label="本页 AI 增长导览"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="ai-customer-service-heading">
                  <span>
                    <Bot size={14} /> AI顾问
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    aria-label="收起 AI顾问"
                    title="收起"
                  >
                    <ChevronDown size={17} />
                  </button>
                </div>
                <span className="ai-customer-service-label">{intro.label}</span>
                <strong>{intro.title}</strong>
                <p>{intro.description}</p>
                <button
                  className="ai-customer-service-action"
                  type="button"
                  onClick={handleOpenChat}
                >
                  <span>向 AI顾问提问</span>
                  <ArrowUpRight size={16} />
                </button>
              </motion.aside>
            )}
          </AnimatePresence>,
          document.body,
        )}
      <div className="ai-customer-service">
        <button
          className="floating-contact-button ai-customer-service-trigger"
          type="button"
          onClick={handleOpenChat}
          aria-label="向 AI顾问提问"
          title="向 AI顾问提问"
        >
          <Bot size={20} />
          <span>AI顾问</span>
        </button>
      </div>
      {!onOpenChat && (
        <CustomerChat
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          initialPrompt={intro?.prompt}
        />
      )}
    </>
  );
}
